import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { PageHeader } from '../layout/PageHeader';
import { MultiSelectFilter } from '../ui/MultiSelectFilter';
import { SelectField } from '../ui/SelectField';
import {
  applyCounterClick,
  ENGAGEMENT_FILTER_OPTIONS,
  isCounterActive,
  isEmailOnlyEngagementFilter,
  matchesAllEngagementFilters,
  type CounterKey,
  type EngagementFilter,
  type RecipientFilterState,
} from '../../lib/broadcastEngagementFilter';
import {
  matchesAllPageViewFilters,
  PAGE_VIEW_FILTER_OPTIONS,
  type PageViewFilter,
} from '../../lib/pageViewFilter';
import { renderBroadcastEmailHtmlClient } from '../../lib/sanitizeHtmlClient';
import type {
  ChannelCounters,
  PageViewCounters,
  SendResultsChannel,
  SendResultsData,
  SendResultsRecipient,
  SendResultsSendStatus,
  SendResultsStatusTone,
} from '../../types/sendResults';

// Stage 6A v5 — the single, data-driven send-results report. Both the broadcast
// and preview surfaces build a SendResultsData (via their own adapter) and
// render it here. No surface-specific branching beyond reading the data:
// page-views card renders when `pageViews` is set, duplicate/retry buttons when
// `actions` provide them, etc. Extracted verbatim from the v3/v4 BroadcastDetail
// so the broadcast report renders identically.

const STATUS_TONE_BADGE: Record<SendResultsStatusTone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  progress: 'bg-blue-100 text-blue-700',
  info: 'bg-indigo-100 text-indigo-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-700',
};

const SEND_STATUS_BADGE: Record<SendResultsSendStatus, string> = {
  queued: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  bounced: 'bg-amber-100 text-amber-800',
};

function pct(n: number, d: number): string {
  if (d === 0) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function SendResultsView({
  data,
  recipientsLoading = false,
  backHref,
}: {
  data: SendResultsData;
  recipientsLoading?: boolean;
  /** When set, renders a "← Back" link above the header. Each surface supplies
   *  a context-appropriate destination (the broadcast list / the owning
   *  rollout) rather than relying on browser history, so a deep-linked report
   *  still has somewhere to go (v5 §2.1). Omit to hide the affordance. */
  backHref?: string;
}) {
  const [channelFilter, setChannelFilter] = useState<'all' | SendResultsChannel>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | SendResultsSendStatus>('all');
  const [engagementFilter, setEngagementFilter] = useState<EngagementFilter[]>([]);
  // Preview-only page-view filter dimension (§2.5).
  const [pageViewFilter, setPageViewFilter] = useState<PageViewFilter[]>([]);
  const isPreview = data.source === 'preview';

  // v4 §Change-2 — the engagement dropdown stays open under Channel = SMS; only
  // the email-only options are individually disabled. Switching to SMS prunes
  // any stale email-only selections so the table doesn't go empty.
  const setChannel = (next: 'all' | SendResultsChannel) => {
    setChannelFilter(next);
    if (next === 'sms') {
      setEngagementFilter((prev) => prev.filter((f) => !isEmailOnlyEngagementFilter(f)));
    }
  };

  const engagementOptions = ENGAGEMENT_FILTER_OPTIONS.map((o) => ({
    ...o,
    disabled: channelFilter === 'sms' && isEmailOnlyEngagementFilter(o.value),
    disabledTitle: 'Not tracked for SMS',
  }));

  // v4 §Change-3 — the three dropdowns are one shared filter state; clicking a
  // stat counter drives all three at once (replace, or toggle off).
  const currentFilter: RecipientFilterState = {
    channel: channelFilter,
    status: statusFilter,
    engagement: engagementFilter,
  };
  const onCounterClick = (key: CounterKey, channel: SendResultsChannel) => {
    const next = applyCounterClick(key, channel, currentFilter);
    setChannelFilter(next.channel);
    setStatusFilter(next.status);
    setEngagementFilter(next.engagement);
    setPageViewFilter([]);
  };

  // Page-views card lines (§2.6) behave like counters: clicking sets a clean
  // state filtered to that page-view option; the active line is highlighted by
  // exact-equality and clicking it again clears. Counter clicks (above) clear
  // the page-view filter for the same reason.
  const isCardLineActive = (option: PageViewFilter) =>
    channelFilter === 'all' &&
    statusFilter === 'all' &&
    engagementFilter.length === 0 &&
    pageViewFilter.length === 1 &&
    pageViewFilter[0] === option;
  const onCardLineClick = (option: PageViewFilter) => {
    const clear = isCardLineActive(option);
    setChannelFilter('all');
    setStatusFilter('all');
    setEngagementFilter([]);
    setPageViewFilter(clear ? [] : [option]);
  };

  const rows = useMemo(() => {
    return data.recipients
      .filter((r) => channelFilter === 'all' || r.channel === channelFilter)
      .filter((r) => statusFilter === 'all' || r.status === statusFilter)
      .filter((r) => {
        if (engagementFilter.length === 0) return true;
        // SMS rows can't satisfy an email-only filter (no open/click tracking),
        // so they're excluded as soon as one is active. Delivery-outcome filters
        // alone (bounced/failed/unsubscribed) DO apply to SMS rows.
        if (r.channel === 'sms' && engagementFilter.some(isEmailOnlyEngagementFilter)) return false;
        return matchesAllEngagementFilters(r, engagementFilter);
      })
      .filter((r) => pageViewFilter.length === 0 || matchesAllPageViewFilters(r, pageViewFilter))
      .sort((a, b) => a.contactName.localeCompare(b.contactName));
  }, [data.recipients, channelFilter, statusFilter, engagementFilter, pageViewFilter]);

  const wantsEmail = data.channels.includes('email');
  const wantsSms = data.channels.includes('sms');
  const dup = data.actions.duplicate;
  const del = data.actions.deleteAction;
  const retryAll = data.actions.retryAllFailed;
  const retryRecipient = data.actions.retryRecipient;
  const colCount = 7 + (isPreview ? 1 : 0) + (retryRecipient ? 1 : 0);

  return (
    <div className="max-w-5xl">
      <PageHeader
        title={data.title}
        description={data.audienceDescription}
        backHref={backHref}
        actions={
          <>
            {dup && (
              <Button variant="outline" onClick={dup.onClick} disabled={dup.pending}>
                {dup.pending ? 'Duplicating…' : 'Duplicate'}
              </Button>
            )}
            {del && (
              <Button variant="destructive" onClick={del.onClick} disabled={del.pending}>
                {del.pending ? 'Deleting…' : 'Delete'}
              </Button>
            )}
            {retryAll && retryAll.failedCount > 0 && (
              <Button variant="outline" onClick={retryAll.onClick} disabled={retryAll.pending}>
                {retryAll.pending ? 'Retrying…' : `Retry all failed (${retryAll.failedCount})`}
              </Button>
            )}
            <span
              className={`rounded px-2 py-1 text-sm font-medium ${STATUS_TONE_BADGE[data.status.tone]}`}
            >
              {data.status.label}
            </span>
          </>
        }
      />

      {dup?.errorText && <p className="mb-3 text-sm text-red-600">{dup.errorText}</p>}
      {del?.errorText && <p className="mb-3 text-sm text-red-600">{del.errorText}</p>}
      {retryAll?.errorText && <p className="mb-3 text-sm text-red-600">{retryAll.errorText}</p>}

      <p className="mb-4 text-xs text-slate-500">
        {data.sentAt ? `Sent ${fmt(data.sentAt)}` : 'Not sent yet'}
        {data.fromMode ? ` · From mode: ${data.fromMode}` : ''}
        {data.isInFlight && ' · refreshing every 10s…'}
      </p>

      {/* Per-channel summary counters. One row per channel present in the data;
          SMS renders — for the Opened / Clicked cells (no open/click tracking).
          Counters are clickable (§v4): click to filter the table to that metric
          + channel; click the highlighted one again to clear. */}
      <div className="mb-6 space-y-3">
        {data.counters.email && (
          <ChannelStatRow
            channel="email"
            label="Email"
            counters={data.counters.email}
            currentFilter={currentFilter}
            onCounterClick={onCounterClick}
          />
        )}
        {data.counters.sms && (
          <ChannelStatRow
            channel="sms"
            label="SMS"
            counters={data.counters.sms}
            currentFilter={currentFilter}
            onCounterClick={onCounterClick}
          />
        )}
      </div>

      {/* Per-channel breakdown cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {data.counters.email && (
          <div className="rounded-md border border-slate-200 p-4">
            <h3 className="mb-2 font-semibold">Email</h3>
            <dl className="space-y-1 text-sm">
              <Rate label="Sent" value={String(data.counters.email.sent)} />
              <Rate
                label="Delivery rate"
                value={pct(data.counters.email.delivered, data.counters.email.sent)}
              />
              <Rate
                label="Open rate"
                value={pct(data.counters.email.opened ?? 0, data.counters.email.delivered)}
              />
              <Rate
                label="Click rate"
                value={pct(data.counters.email.clicked ?? 0, data.counters.email.delivered)}
              />
            </dl>
          </div>
        )}
        {data.counters.sms && (
          <div className="rounded-md border border-slate-200 p-4">
            <h3 className="mb-2 font-semibold">SMS</h3>
            <dl className="space-y-1 text-sm">
              <Rate label="Sent" value={String(data.counters.sms.sent)} />
              <Rate
                label="Delivery rate"
                value={pct(data.counters.sms.delivered, data.counters.sms.sent)}
              />
            </dl>
          </div>
        )}
      </div>

      {data.pageViews && (
        <PageViewsCard
          pageViews={data.pageViews}
          isActive={isCardLineActive}
          onLineClick={onCardLineClick}
        />
      )}

      <MessagePreviewPanel
        messagePreview={data.messagePreview}
        wantsEmail={wantsEmail}
        wantsSms={wantsSms}
      />

      {/* Recipient table */}
      <div className="mb-2 flex items-center gap-3">
        <h3 className="font-semibold">Recipients ({data.recipients.length})</h3>
        <SelectField
          size="sm"
          aria-label="Filter by channel"
          value={channelFilter}
          onValueChange={(v) => setChannel(v as typeof channelFilter)}
          options={[
            { value: 'all', label: 'All channels' },
            { value: 'email', label: 'Email' },
            { value: 'sms', label: 'SMS' },
          ]}
        />
        <SelectField
          size="sm"
          aria-label="Filter by status"
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'queued', label: 'Queued' },
            { value: 'sent', label: 'Sent' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'bounced', label: 'Bounced' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
        <MultiSelectFilter
          label="Engagement"
          options={engagementOptions}
          selected={engagementFilter}
          onChange={(next) => setEngagementFilter(next as EngagementFilter[])}
          debounceMs={0}
          chipThreshold={2}
        />
        {isPreview && (
          <MultiSelectFilter
            label="Page views"
            options={PAGE_VIEW_FILTER_OPTIONS}
            selected={pageViewFilter}
            onChange={(next) => setPageViewFilter(next as PageViewFilter[])}
            debounceMs={0}
            chipThreshold={2}
          />
        )}
      </div>

      {recipientsLoading ? (
        <p className="text-sm text-slate-500">Loading recipients…</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">To</th>
                <th className="px-3 py-2 font-medium">Channel</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Delivered</th>
                <th className="px-3 py-2 font-medium">Opened</th>
                <th className="px-3 py-2 font-medium">Clicks</th>
                {isPreview && <th className="px-3 py-2 font-medium">Preview views</th>}
                {retryRecipient && <th className="px-3 py-2 font-medium" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-3 py-3 text-slate-400">
                    No recipients match.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={`${r.contactId}-${r.channel}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-900">{r.contactName}</td>
                    <td className="px-3 py-2 text-slate-600">{r.to || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.channel}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${SEND_STATUS_BADGE[r.status]}`}
                      >
                        {r.status}
                      </span>
                      {r.failureReason && (
                        <span className="ml-1 text-xs text-slate-400" title={r.failureReason}>
                          ⓘ
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{fmt(r.deliveredAt)}</td>
                    <td className="px-3 py-2 text-slate-600">{fmt(r.openedAt)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.clickCount || '—'}</td>
                    {isPreview && (
                      <td
                        className="px-3 py-2 text-slate-600"
                        title={r.lastPageViewAt ? `Last viewed ${fmt(r.lastPageViewAt)}` : 'Never viewed'}
                      >
                        {r.pageViewCount ?? 0}
                      </td>
                    )}
                    {retryRecipient && (
                      <td className="px-3 py-2 text-right">
                        {(r.status === 'failed' || r.status === 'bounced') && (
                          <button
                            type="button"
                            onClick={() => retryRecipient(r)}
                            className="text-xs text-sky-700 hover:text-sky-900"
                          >
                            Retry
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Read-only template preview (broadcast §1.2). Hidden entirely when the data
// carries no message content (the preview case — no stored AM template, v5 Q2).
function MessagePreviewPanel({
  messagePreview,
  wantsEmail,
  wantsSms,
}: {
  messagePreview: SendResultsData['messagePreview'];
  wantsEmail: boolean;
  wantsSms: boolean;
}) {
  const has =
    messagePreview.emailSubject !== undefined ||
    messagePreview.emailBodyMarkdown !== undefined ||
    messagePreview.smsBody !== undefined;
  const emailHtml = useMemo(
    () =>
      wantsEmail && messagePreview.emailBodyMarkdown !== undefined
        ? renderBroadcastEmailHtmlClient(messagePreview.emailBodyMarkdown)
        : '',
    [wantsEmail, messagePreview.emailBodyMarkdown],
  );
  if (!has) return null;

  return (
    <div className="mb-6">
      <h3 className="mb-1 font-semibold">Message preview</h3>
      <p className="mb-3 text-xs text-slate-500">
        The composed template — slugs like <code>{'{first_name}'}</code> are shown unresolved.
      </p>
      <div className="space-y-4">
        {wantsEmail && messagePreview.emailBodyMarkdown !== undefined && (
          <div className="rounded-md border border-slate-200">
            <div className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
              Subject:{' '}
              <span className="font-medium text-slate-900">
                {messagePreview.emailSubject || '(no subject)'}
              </span>
            </div>
            <div
              className="prose prose-sm max-w-none px-3 py-2 text-sm"
              // eslint-disable-next-line react/no-danger -- markdown rendered + sanitised client-side.
              dangerouslySetInnerHTML={{ __html: emailHtml }}
            />
          </div>
        )}
        {wantsSms && messagePreview.smsBody !== undefined && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">SMS</p>
            <div className="max-w-xs whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-2 text-sm text-slate-900">
              {messagePreview.smsBody || '(empty)'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Preview-only page-views card (§2.2). Total views is a static line; the other
// three filter the recipient table (clickable, with exact-equality highlight).
function PageViewsCard({
  pageViews,
  isActive,
  onLineClick,
}: {
  pageViews: PageViewCounters;
  isActive: (option: PageViewFilter) => boolean;
  onLineClick: (option: PageViewFilter) => void;
}) {
  const total = pageViews.recipientsWithViews + pageViews.recipientsWithoutViews;
  return (
    <div className="mb-6 max-w-sm rounded-md border border-slate-200">
      <div className="border-b border-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
        Preview page views
      </div>
      <dl className="divide-y divide-slate-100 text-sm">
        <div className="flex items-center justify-between px-4 py-2">
          <dt className="text-slate-500">Total views</dt>
          <dd className="font-medium text-slate-900">{pageViews.totalViews}</dd>
        </div>
        <PageViewsLine
          label="Unique viewers"
          value={`${pageViews.recipientsWithViews} / ${total} (${pct(
            pageViews.recipientsWithViews,
            total,
          )})`}
          active={isActive('has_views')}
          onClick={() => onLineClick('has_views')}
        />
        <PageViewsLine
          label="Multiple viewers"
          value={`${pageViews.recipientsWithMultipleViews} (${pct(
            pageViews.recipientsWithMultipleViews,
            total,
          )})`}
          active={isActive('multiple_views')}
          onClick={() => onLineClick('multiple_views')}
        />
        <PageViewsLine
          label="Never viewed"
          value={String(pageViews.recipientsWithoutViews)}
          active={isActive('no_views')}
          onClick={() => onLineClick('no_views')}
        />
      </dl>
    </div>
  );
}

function PageViewsLine({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex w-full items-center justify-between px-4 py-2 text-left transition-colors ${
        active ? 'bg-blue-50 ring-1 ring-inset ring-blue-400' : 'hover:bg-slate-50'
      }`}
    >
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </button>
  );
}

// The seven stat counters, in display order. opened/clicked read from the
// optional ChannelCounters fields — undefined (SMS) renders a static em-dash.
const COUNTER_CELLS: { key: CounterKey; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'failed', label: 'Failed' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
];

function ChannelStatRow({
  channel,
  label,
  counters,
  currentFilter,
  onCounterClick,
}: {
  channel: SendResultsChannel;
  label: string;
  counters: ChannelCounters;
  currentFilter: RecipientFilterState;
  onCounterClick: (key: CounterKey, channel: SendResultsChannel) => void;
}) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-7">
        {COUNTER_CELLS.map(({ key, label: cellLabel }) => {
          const raw = counters[key as keyof ChannelCounters];
          // opened/clicked may be undefined (SMS) — render a static em-dash.
          const value = raw === undefined ? null : raw;
          return (
            <Counter
              key={key}
              label={cellLabel}
              value={value}
              active={value !== null && isCounterActive(key, channel, currentFilter)}
              onClick={value === null ? undefined : () => onCounterClick(key, channel)}
            />
          );
        })}
      </div>
    </div>
  );
}

// A stat counter. Clickable counters (value !== null) drive the recipient-table
// filter and highlight when active; the SMS Opened/Clicked cells (value null)
// render a static em-dash.
function Counter({
  label,
  value,
  active = false,
  onClick,
}: {
  label: string;
  value: number | null;
  active?: boolean;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="text-xl font-semibold text-slate-900">{value === null ? '—' : value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </>
  );
  if (!onClick) {
    return <div className="rounded-md border border-slate-200 px-3 py-2 text-center">{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`w-full rounded-md border px-3 py-2 text-center transition-colors cursor-pointer ${
        active
          ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      {inner}
    </button>
  );
}

function Rate({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{value}</dd>
    </div>
  );
}
